import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../redux/store';
import { fetchAlbums } from '../redux/thunks/albumThunks';
import { uploadSingleImage, uploadMultipleImages } from '../redux/thunks/imageThunks';
import Sidebar from './Sidebar';
import NavbarApp from './navbarApp';
import { resetStatus } from '../redux/slices/imageSlice';
type Album = {
    albumId: string;
    albumName: string;
  };

  
const UploadImages: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const albumState = useSelector((state: RootState) => state.albums);
    const authState = useSelector((state: RootState) => state.auth);
    const imageState = useSelector((state: RootState) => state.images);
    const user = authState.user;

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [album, setAlbum] = useState<Album | null>(null);
    const [uploadType, setUploadType] = useState<'single' | 'multiple'>('single');
    const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);

    const albums = albumState.list.albums || [];
    const filteredAlbums = albums.length === 1
        ? (albums[0].title.toLowerCase() === 'demo' ? [] : albums)
        : albums.filter(album => album.title.toLowerCase() !== 'demo');

    const noValidAlbums = filteredAlbums.length === 0;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedFiles(e.target.files);
    };

    const handleUpload = async () => {
        if (!album?.albumId || !selectedFiles || selectedFiles.length === 0) {
            alert('Please select an album and file(s).');
            return;
        }
        
        const formData = new FormData();
        formData.append('albumId', album.albumId.toString());
        formData.append('albumName', album.albumName.toString());

        try {
            if (uploadType === 'single') {
                formData.append('image', selectedFiles[0]);
                await dispatch(uploadSingleImage(formData)).unwrap();
            } else {
                Array.from(selectedFiles).forEach((file) => {
                    formData.append('images', file);
                });
                await dispatch(uploadMultipleImages(formData)).unwrap();
            }
            alert('Upload successful!');
            dispatch(resetStatus());
            setSelectedFiles(null);
        } catch (error: any) {
            console.error('Upload failed:', error);
            alert(error?.message || 'Upload failed');
        }
    };

    useEffect(() => {
        if (user?.id && albumState.status === 'idle') {
            dispatch(fetchAlbums({ customerId: user.id }));
        }
    }, [dispatch, user, albumState.status]);

    return (
        <div className="min-h-screen text-white bg-black flex flex-col">
            <NavbarApp user={user} setSidebarOpen={setSidebarOpen} />
            <div className="flex flex-1">
                <div className="hidden md:block w-64 bg-gray-900">
                    <Sidebar />
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                    <h1 className="text-xl font-bold mb-6">Upload Images</h1>

                    {noValidAlbums ? (
                        <p className="text-red-500 mb-4">
                            No valid albums available for upload. Please create an album first.
                        </p>
                    ) : (
                        <>
                            <div className="mb-4">
                                <label className="block mb-2">Select Album:</label>
                                <select
                                    value={album?.albumId ?? ''}
                                    onChange={(e) => {
                                        const selected = filteredAlbums.find(album => album.albumId.toString() === e.target.value);
                                        if (selected) {
                                        setAlbum({
                                            albumId: selected.albumId,
                                            albumName: selected.title,
                                        });
                                        } else {
                                        setAlbum(null);
                                        }
                                    }}
                                    className="text-black px-4 py-2 rounded w-full"
                                    >
                                    <option value="">Select Album</option>
                                    {filteredAlbums.map((album) => (
                                        <option key={album.albumId} value={album.albumId}>
                                        {album.title}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-4">
                                <label className="block mb-2">Upload Type:</label>
                                <div className="flex gap-4">
                                    <label>
                                        <input
                                            type="radio"
                                            value="single"
                                            checked={uploadType === 'single'}
                                            onChange={() => setUploadType('single')}
                                            className="mr-2"
                                        />
                                        Single Image
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            value="multiple"
                                            checked={uploadType === 'multiple'}
                                            onChange={() => setUploadType('multiple')}
                                            className="mr-2"
                                        />
                                        Multiple Images
                                    </label>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="block mb-2">Select Image{uploadType === 'multiple' && 's'}:</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple={uploadType === 'multiple'}
                                    onChange={handleFileChange}
                                    className="text-white"
                                />
                            </div>

                            <button
                                onClick={handleUpload}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded"
                                disabled={noValidAlbums || imageState.status === 'loading'}
                            >
                                {imageState.status === 'loading' ? 'Uploading...' : 'Upload'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UploadImages;
